// import { useEffect } from 'react';
import { LanguageProvider, useLanguageContext } from './i18n/language-context';
import './i18n/i18n';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'components/ui/toaster';
import { ClientMiddleware } from 'state/client-middleware';
import MainLayout from 'pages/main/main-layout';
import { AuthLayout } from './pages/auth/auth-layout';
import { SigninPage } from 'pages/auth/signin/signin-page';
import { SignupPage } from 'pages/auth/signup/signup-page';
import { EmailVerification } from 'pages/auth/email-verification/email-verification';
import { SetPasswordPage } from './pages/auth/set-password/set-password';
import { ActivationSuccess } from './pages/auth/activation-success/activation-success';
import { VerificationFailed } from './pages/auth/verification-failed/verification-failed';
import { ResetPasswordPage } from './pages/auth/reset-password/reset-password';
import { ForgotPasswordPage } from './pages/auth/forgot-password/forgot-password';
import { IamTablePage } from './pages/main/iam-table';
import { Profile } from './pages/profile/profile';
import { ThemeProvider } from './components/core/theme-provider';
import { SidebarProvider } from 'components/ui/sidebar';
import { VerifyOtpKey } from './pages/auth/verify-otp-key/verify-otp-key';
import ChatPage from 'pages/chat/chat';
import ProjectChatPage from 'pages/chat/project-chat';
import HomePage from './pages/home/home';
import SignupEmailSentPage from 'pages/auth/signup-email-sent/signup-email-sent';
import { RedirectPage } from './pages/auth/redirect/redirect';
import GroupInvitePage from 'pages/group-invite/group-invite';
import { LoadingOverlay } from './components/core/loading-overlay';
import { NoAuthMiddleware } from './state/no-auth-middleware';
import UserHolder from 'pages/user-holder';
import { PostAuthMiddleware } from './state/post-auth-middleware';
import { NotificationMiddleware } from './state/notification-middleware';
// import { CollaborationWebSocketMiddleware } from './state/collaboration-websocket-middleware';
import { OrganizationTablePage } from './pages/main/organization-table';
import { SubscriptionRouteGuard } from './components/core/subscription-route-guard';
import { SignupRouteGuard } from './components/core/signup-route-guard';
import { SubscriptionPage } from './pages/subscription/subscription';
import { CheckoutPage } from './pages/checkout/checkout';
import { PaymentSuccessPage } from './pages/payment/payment-success';
import { PaymentCancelledPage } from './pages/payment/payment-cancelled';
import { PaymentFailedPage } from './pages/payment/payment-failed';
import { FileManagerMyFiles } from './pages/file-manager/my-files';
import ProjectPage from './pages/project/project-page';
import DocumentManagementPage from './pages/document-manager/document-manager';
import PdfRouteViewer from './features/file-manager/components/pdf-route-viewer';
import { usePageTracking, useSessionTracker, useTrackPageLoadComplete } from './analytics/tracker';
import CustomGptPage from 'pages/custom-gpt/custom-gpt-page';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: 0,
    },
  },
});

function ExternalRedirect({ to }: { to: string }) {
  window.location.replace(to);
  return null;
}

function AppContent() {
  const { isLoading } = useLanguageContext();
  usePageTracking();
  useTrackPageLoadComplete();
  useSessionTracker();

  if (isLoading) {
    return <LoadingOverlay />;
  }
  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      {/* <RedirectHandler /> */}
      <ClientMiddleware>
        <ThemeProvider defaultTheme="dark">
          <SidebarProvider>
            <Routes>
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<SigninPage />} />
                <Route path="/signup" element={
                  <SignupRouteGuard>
                    <SignupPage />
                  </SignupRouteGuard>
                } />
                <Route path="/sent-email" element={<EmailVerification />} />
                <Route path="/activate" element={<SetPasswordPage />} />
                <Route path="/resetpassword" element={<ResetPasswordPage />} />
                <Route path="/success" element={<ActivationSuccess />} />
                <Route path="/activate-failed" element={<VerificationFailed />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verify-key" element={<VerifyOtpKey />} />
                <Route path="/signup-email-sent" element={
                  <SignupRouteGuard>
                    <SignupEmailSentPage />
                  </SignupRouteGuard>
                } />
                <Route path="/redirect" element={<RedirectPage />} />
              </Route>
              <Route path="/group-invite/:token" element={<GroupInvitePage />} />
              <Route
                path="/promotion"
                element={
                  <ExternalRedirect to={process.env.REACT_APP_PUBLIC_PROMOTION_PAGE_URL ?? ''} />
                }
              />

              <Route element={
                <PostAuthMiddleware>
                  <NotificationMiddleware>
                    {/* <CollaborationWebSocketMiddleware> */}
                    <UserHolder />
                    {/* </CollaborationWebSocketMiddleware> */}
                  </NotificationMiddleware>
                </PostAuthMiddleware>
              }>
                <Route element={<MainLayout />}>

                  <Route path="/profile" element={<Profile />} />
                  <Route path="/identity-management" element={<IamTablePage />} />
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/chat/:conversationId/:projectId" element={<ProjectChatPage />} />
                  <Route path="/chat/:conversationId" element={<ChatPage />} />
                  <Route path="/project/:projectId" element={<ProjectPage />} />
                  <Route path="/custom-gpts/:customGptId" element={<CustomGptPage />} />
                  <Route path="/organization-management" element={<OrganizationTablePage />} />
                  <Route path="/media-library" element={<FileManagerMyFiles />} />
                  <Route path="/document-management" element={<DocumentManagementPage />} />
                </Route>
                <Route path="/subscription" element={
                  <SubscriptionRouteGuard>
                    <SubscriptionPage />
                  </SubscriptionRouteGuard>
                } />
                <Route path="/checkout" element={
                  <SubscriptionRouteGuard>
                    <CheckoutPage />
                  </SubscriptionRouteGuard>
                } />
                <Route path="/payment-success" element={<PaymentSuccessPage />} />
                <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
                <Route path="/payment-failed" element={<PaymentFailedPage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/" element={<Navigate to="/home" />} />
                <Route path="/pdf-viewer" element={<PdfRouteViewer />} />
              </Route>
            </Routes>
          </SidebarProvider>
        </ThemeProvider>
      </ClientMiddleware>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <NoAuthMiddleware>
          <LanguageProvider defaultLanguage={new URLSearchParams(window.location.search).get('lang') || 'de-DE'} defaultModules={['common', 'auth', 'profile']}>
            <AppContent />
          </LanguageProvider>
        </NoAuthMiddleware>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

// 2 coupon and a  extra card ,subscription page a plan details,

export default App;
